import { Button } from "@/components/flexkit/Button";
import { Nudge } from "@/components/nudges/Nudge";
import { Text } from "@/components/podkit/typography/Text";

import { useAutoDeleteValues } from "@/hooks/use-auto-delete-values";
import { duration } from "@/format/time";
import { useCallback, useEffect, useState, useMemo, type FC } from "react";
import { TrackLocations } from "@/hooks/use-segment";
import { NudgeKeys, useNudges } from "@/hooks/use-nudges";
import { isOrgPolicyMoreRestrictive } from "@/utils/auto-delete";

export const AutoDeletePolicyNudge: FC = () => {
    const { active: nudge, error, isPending } = useNudges();
    const { currentAutoDeletePolicyValue, userPreferenceValue, setPreviousAutoDeletePolicyValue } =
        useAutoDeleteValues();
    const [open, setOpen] = useState(false);

    const description = useMemo(() => {
        if (currentAutoDeletePolicyValue === userPreferenceValue) {
            return "All good - your settings remain unchanged!";
        }
        if (isOrgPolicyMoreRestrictive(currentAutoDeletePolicyValue, userPreferenceValue)) {
            const formattedTime = duration(currentAutoDeletePolicyValue, "long", "extended");
            return `Archived environments will be auto-deleted after ${formattedTime}`;
        }
        const formattedTime = duration(userPreferenceValue, "long", "extended");
        return `Archived environments will be auto-deleted after ${formattedTime}`;
    }, [currentAutoDeletePolicyValue, userPreferenceValue]);

    // Auto-show nudge if applicable
    useEffect(() => {
        if (isPending || error) {
            return;
        }

        if (nudge?.key === NudgeKeys.AUTO_DELETE_POLICY) {
            setOpen(true);
            return;
        }
        setOpen(false);
    }, [isPending, error, nudge?.key]);

    const handleDismiss = useCallback(() => {
        setOpen(false);
        // Update the stored org policy value so we can detect future changes
        setPreviousAutoDeletePolicyValue(String(currentAutoDeletePolicyValue));
    }, [setPreviousAutoDeletePolicyValue, currentAutoDeletePolicyValue]);

    if (!open) {
        return null;
    }

    return (
        <Nudge
            open={open}
            toggleOpen={handleDismiss}
            ariaLabelPrefix="Announcement - Auto-delete Policy Update"
            showCloseButton={false}
            data-testid="nudge-auto-delete-policy-trigger"
            data-track-location={TrackLocations.AutoDeletePolicyNudge}
        >
            <div className="flex flex-col gap-2">
                <div className="p-2 text-center">
                    <Text className="text-base font-bold text-content-primary">
                        Your organization has updated its auto-delete policy
                    </Text>
                    <Text className="text-base text-content-secondary">{description}</Text>
                </div>
                <Button
                    variant="secondary"
                    size="md"
                    className="grow"
                    aria-label="Announcement - Auto-delete Policy Update - Dismiss"
                    onClick={handleDismiss}
                    data-tracking-id="dismiss-announcement-auto-delete-policy-update"
                >
                    Dismiss
                </Button>
            </div>
        </Nudge>
    );
};
